import { ApolloCache } from '@apollo/client'
import { produce } from 'immer'
import { Project, Survey } from '../../../data/gql-gen/dashboard/graphql'
import { SearchProjects } from '../../../modules/Project/Project.query'

/**
 * Appends a newly created market (Survey) to the cached project so the views
 * that read it reflect it without a refetch — notably the master
 * questionnaire's market-visibility dialog, which reads `projectBySurveyId`
 * (cache-first), and the projects list (`searchProjects`).
 *
 * Idempotent (skips if the survey is already present) and scoped to the given
 * `projectId`. Mirrors `PauseSurveyCacheUpdate`.
 */
export const AddMarketCacheUpdate = (
  cache: ApolloCache<unknown>,
  projectId: string,
  newSurvey: Survey
): void => {
  const appendSurvey = (surveys: Survey[] = []): Survey[] =>
    surveys.some((survey) => survey.surveyId === newSurvey.surveyId)
      ? surveys
      : [...surveys, newSurvey]

  cache.modify({
    fields: {
      // projectById (PROJECT_BY_ID — used by the multi-market pages). Patch it
      // too so every project read stays consistent without relying on a refetch.
      project(existing) {
        const project = existing as Project | undefined
        if (!project || project.projectId !== projectId) {
          return existing
        }
        return produce(project, (draft) => {
          draft.surveys = appendSurvey(draft.surveys)
        })
      },
      // Keyed per surveyId; runs for every cached entry. Entries for this
      // project share its surveys, so append to each (incl. the master entry
      // the questionnaire dialog reads).
      projectBySurveyId(existing) {
        const project = existing as Project | undefined
        if (!project || project.projectId !== projectId) {
          return existing
        }
        return produce(project, (draft) => {
          draft.surveys = appendSurvey(draft.surveys)
        })
      },
      searchProjects(existing) {
        const searchProjects = existing as SearchProjects | undefined
        const index = searchProjects?.projects.findIndex(
          (project) => project.projectId === projectId
        )
        if (!searchProjects || index === undefined || index < 0) {
          return existing
        }
        return produce(searchProjects, (draft) => {
          draft.projects[index].surveys = appendSurvey(
            draft.projects[index].surveys
          )
        })
      }
    }
  })
}
